'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { StoreReview } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { motion, useAnimationControls } from 'framer-motion';
import { PenLine } from 'lucide-react';
import Link from '@eliadmin/core/src/components/link';
import { FaStar } from 'react-icons/fa';

const GOOGLE_REVIEW_URL =
  'https://www.google.com/search?hl=sk-SK&q=Antol+%C4%BDubom%C3%ADr+-+AL-KLIMA+Recenzie#lkt=LocalPoiReviews';

export function Reviews({
  subtitle,
  title,
  reviews,
  speed = 180,
}: {
  subtitle: string;
  title: string;
  reviews: StoreReview[];
  speed?: number;
}) {
  const { t } = useTranslations();
  const trackRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const [trackWidth, setTrackWidth] = useState(0);
  const [isReducedMotion, setReducedMotion] = useState(false);

  const duplicated = [...reviews, ...reviews, ...reviews];

  const measure = useCallback(() => {
    if (!trackRef.current) return;
    const third = trackRef.current.scrollWidth / 3;
    setTrackWidth(third);
  }, []);

  useEffect(() => {
    measure();
    const handler = () => measure();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measure]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(media.matches);

    const listener = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    if (!trackWidth || isReducedMotion) return;

    controls.set({ x: 0 });
    controls.start({
      x: -trackWidth,
      transition: {
        duration: speed,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  }, [trackWidth, speed, controls, isReducedMotion]);

  if (!reviews.length) return null;

  return (
    <section
      className={cn(
        'ea-google-reviews',
        'custom-section gap-8 px-0 py-10 sm:gap-10 md:py-16 lg:gap-12',
      )}
    >
      <div className={cn('mb-4', 'ea-google-reviews__heading')}>
        <span>{subtitle}</span>
        <h2>{title}</h2>
      </div>

      <div className={cn('', 'ea-google-reviews__viewport')}>
        <motion.div
          ref={trackRef}
          className="flex"
          style={{ willChange: 'transform' }}
          animate={controls}
        >
          {duplicated.map((review, index) => (
            <div key={`${review.id}-${index}`} className="mx-2 flex-shrink-0 sm:mx-3 lg:mx-4">
              <ReviewCard review={review} />
            </div>
          ))}
        </motion.div>
      </div>

      <div className="mt-4 flex w-full items-center justify-center px-4">
        <Button asChild variant="dark" className="w-full rounded-full sm:w-fit">
          <Link
            prefetch={false}
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex gap-4"
          >
            <PenLine aria-hidden="true" className="h-5 w-5" />
            {t('text585')}
          </Link>
        </Button>
      </div>
    </section>
  );
}

function ReviewCard({ review }: { review: StoreReview }) {
  const rating = Number(review.rating) || 5;

  return (
    <div className="flex h-full w-[340px] flex-col items-start justify-start gap-4 rounded-[24px] border border-black/10 bg-white p-4 sm:w-[460px] sm:gap-6 sm:p-5 md:w-[520px] lg:w-[588px] lg:gap-8 lg:p-6">
      <div className="flex w-full items-center justify-between gap-4">
        <p className="text-base font-bold text-black sm:text-lg lg:text-xl">{review.name}</p>
        <div className="flex items-center justify-end gap-0.5">
          {Array.from({ length: 5 }, (_, index) => (
            <FaStar
              key={index}
              className={index < rating ? 'text-primary' : 'text-black/10'}
              size={20}
            />
          ))}
        </div>
      </div>
      <div className="h-[1px] w-full bg-black/10" />
      <HtmlDescription
        content={review.review}
        className={cn('w-full text-black', 'ea-review-card__description')}
        color="foreground"
        size="sm"
      />
    </div>
  );
}
